import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AiOutlineArrowLeft } from 'react-icons/ai';
import apiClient from "../utils/apiClient";
import { orderslist, updateOrderProcessed, updateOrderShipped, deleteOrder, releaseFundsRoute, rejectOrderRoute} from "../utils/APIRoutes";
import { client, urlFor } from "../sanity/sanityClient";
import './OrdersList.css';
import TrackOrder from './TrackOrder';

const OrdersList = () => {
  // SECURITY: the URL used to carry :userId and this component
  // compared it to a hardcoded admin id string, and separately
  // guessed "is this person an agent" by scanning the (entire,
  // unfiltered) order list for a matching agent.userId — all
  // client-side, all trusting a value the browser controls. Dropped
  // entirely: /api/ordered now determines who the caller is from
  // their verified session and returns exactly (and only) the orders
  // and per-order role ("admin" / "agent" / "buyer" / "seller") they
  // should see — see the redesigned handler in app.js. This component
  // just renders what the server already decided.
  const [orders, setOrders] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sanityProducts, setSanityProducts] = useState([]);

  // Cheap, synchronous check for whether there's a session at all —
  // used only to decide whether to attempt the fetch and what to
  // render while we don't yet know; it is NOT what authorizes
  // anything (the backend's own session check does that). Mirrors the
  // same pattern used in useServerRole/ContactsMessaged: skip the
  // network call entirely for a visitor we already know is anonymous,
  // rather than firing a request that can only 401.
  const hasSession = !!localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);

  useEffect(() => {
    if (!hasSession) {
      setLoading(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        const { data } = await apiClient.get(orderslist);
        setOrders(data.orders || []);
        setIsAdmin(!!data.isAdmin);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const query = 
          `*[_type in ["clothingandfashion", "electronics", "arts", "fashion", "salon", "grocery", "beauty", "crops",
         "sports", "kitchen", "hotels", "hardware", "books", "estate", "foodandbeverages", "agritech", "giftoptions",
          "homeandfurnitures","livestock", "vehicles", "jewelryandaccessories", "medical", "useditems", "construction", "kidsandtoys", "hospitality"]] {
            _id,
            image
          }`;
        const result = await client.fetch(query);
        setSanityProducts(result);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, []);

  const getProductImage = (imageKey) => {
    if (!imageKey) return null;

    const product = sanityProducts.find((p) =>
      p.image?.some((img) => img._key === imageKey)
    );

    if (product) {
      const matchedImage = product.image.find((img) => img._key === imageKey);
      return matchedImage ? urlFor(matchedImage).url() : null;
    }

    return null;
  };

  // SECURITY: all five handlers below swapped `fetch` for `apiClient`
  // so the admin-only actions (processed/shipped/delete/release-funds/
  // reject — all gated server-side behind auth + requireAdmin) actually
  // carry the session cookie. They're also only ever rendered when
  // `isAdmin` is true (a value the server told us, not one this
  // component decides), so a non-admin never sees these buttons at
  // all — but the backend gate is what actually matters, this is just
  // keeping the UI honest about it.
  const handleProcessed = async (orderId) => {
    try {
      const { data } = await apiClient.put(`${updateOrderProcessed}/${orderId}`);
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === orderId ? { ...order, status: data.order?.status || 'processed' } : order
        )
      );
    } catch (error) {
      console.error("Error updating order processed status:", error);
    }
  };

  const handleShipped = async (orderId) => {
    try {
      const { data } = await apiClient.put(`${updateOrderShipped}/${orderId}`);
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === orderId ? { ...order, status: data.order?.status || 'shipped' } : order
        )
      );
    } catch (error) {
      console.error("Error updating order shipped status:", error);
    }
  };

  const handleReleaseFunds = async (orderId, itemId) => {
    try {
      const { data } = await apiClient.put(`${releaseFundsRoute}/${orderId}/release-funds`, { itemId });
      if (data.success) {
        setOrders(prev => prev.map(o => (o._id === orderId ? { ...data.order, viewerRelation: o.viewerRelation } : o)));
      }
    } catch (error) {
      console.error("Error releasing funds:", error);
    }
  };

  const handleRejectItem = async (orderId, itemId) => {
    const reason = window.prompt("Reason for rejecting this item (shown to the buyer):");
    if (reason === null) return;
    try {
      const { data } = await apiClient.put(`${rejectOrderRoute}/${orderId}/reject`, { itemId, reason });
      if (data.success) {
        setOrders(prev => prev.map(o => (o._id === orderId ? { ...data.order, viewerRelation: o.viewerRelation } : o)));
      }
    } catch (error) {
      console.error("Error rejecting item:", error);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const confirmDelete = window.confirm("Delete this order permanently?");
    if (!confirmDelete) return;

    try {
      await apiClient.delete(`${deleteOrder}/${orderId}`);
      setOrders(prev => prev.filter(order => order._id !== orderId));
    } catch (error) {
      console.error("Error deleting order:", error);
    }
  };

  // SECURITY: anonymous visitors get a purpose-built message with a
  // path back to login, instead of a silent empty state (previously
  // this would have just fetched, gotten nothing meaningful back, and
  // shown an unhelpful blank "No orders found").
  if (!hasSession) {
    return (
      <div className="orders-container">
        <Link to={'/'}><AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} /></Link>
        <h2 className="heading">📦 Submitted Orders</h2>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Please sign in to view your orders.</p>
          <Link to="/login" style={{ color: '#0f1f3d', fontWeight: 700, textDecoration: 'underline' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-container">
       <Link to={'/'}><AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} /></Link>
      <h2 className="heading">📦 Submitted Orders</h2>
        <TrackOrder/>
      {loading ? (
        <p className="loading">Loading orders...</p>
      ) : orders.length === 0 ? (
        <p className="no-orders">No orders found.</p>
      ) : (
        <div className="orders-grid">
          {[...orders].reverse().map((order) => {
            // Server already scoped cartItems to what this viewer
            // should see (full list for admin/agent/buyer, just their
            // own items for a plain seller) — no client-side filtering
            // needed here anymore.
            const canSeeFullOrder = order.viewerRelation === 'admin' || order.viewerRelation === 'agent' || order.viewerRelation === 'buyer';

            return (
              <div
                key={order._id}
                className={`order-card ${order.status === 'processed' ? 'processed' : ''} ${order.status === 'shipped' ? 'shipped' : ''}`}
              >
                <h3 className="order-title">Order Details</h3>
                <div className="order-info">
                  {/* Always show buyer details */}
                  <p>👤 <strong>Buyer:</strong> {order.fullName}</p>
                  <p>📧 <strong>Email:</strong> {order.email}</p>
                  <p>📞 <strong>Phone:</strong> {order.phone}</p>
                  <p>📍 <strong>Address:</strong> {order.address}</p>
                  
                  {/* Show additional fields for admin, agent, and the buyer themselves */}
                  {canSeeFullOrder && (
                    <>
                      <p>💰 <strong>Total Price:</strong> {order.cartItems[0]?.currency || 'Tsh'} {order.totalPrice.toLocaleString()}</p>
                      <p>📏 <strong>Size/Color:</strong> {order.size}</p>
                      <p>🔄 <strong>Transaction ID:</strong> {order.transactionId}</p>
                    </>
                  )}
                  
                  <p>🗓️ <strong>Order Date:</strong> {new Date(order.orderDate).toLocaleString()}</p>
                  
                  {/* Show agent info for admin and the agent themselves */}
                  {order.agent && (order.viewerRelation === 'admin' || order.viewerRelation === 'agent') && (
                    <>
                      <p>👨‍💼 <strong>Agent:</strong> {order.agent.name}</p>
                      <p>📱 <strong>Agent Phone:</strong> {order.agent.phone}</p>
                      <p>🌍 <strong>Agent Location:</strong> {order.agent.location}</p>
                    </>
                  )}
                </div>

                <div className="ordered-items">
                  <h3 className="items-title">🛒 Ordered Items:</h3>
                  <div className="items-list">
                    {order.cartItems.map((item, index) => {
                      const imageUrl = getProductImage(item.image[0]?._key);

                      return (
                        <div key={index} className="ordered-item">
                          {imageUrl && (
                            <img
                              src={imageUrl}
                              alt={item.name}
                              className="item-image"
                            />
                          )}
                          <div className="item-details">
                            <p className="item-name">{item.name}</p>
                            <p className="item-quantity">📦 {item.quantity} pcs</p>
                            <p className="item-price">💰 {item.price.toLocaleString()} {item.currency || 'Tsh'}</p>
                            {/* Show seller info for admin and agent views */}
                            {(order.viewerRelation === 'admin' || order.viewerRelation === 'agent') && (
                              <>
                                {item.sellerName && <p className="item-seller">Seller: {item.sellerName}</p>}
                                {item.sellerPhone && <p className="item-seller">Seller Contact: {item.sellerPhone}</p>}
                              </>
                            )}
                            {isAdmin && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                              {item.fundsReleased ? (
                                <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>Funds Released</span>
                              ) : item.rejected ? (
                                <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 700 }}>Rejected</span>
                              ) : (
                                <>
                                  <button
                                    className="processed-btn"
                                    disabled={!item.receivedByBuyer}
                                    onClick={() => handleReleaseFunds(order._id, item._id)}
                                  >
                                    {item.receivedByBuyer ? 'Release Funds' : 'Awaiting Buyer Confirmation'}
                                  </button>
                                  <button className="delete-btn" onClick={() => handleRejectItem(order._id, item._id)}>
                                    Reject Item
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          </div>
                        </div>
                      );

                    })}
                  </div>
                </div>

                {/* Show action buttons only for admin */}
                {isAdmin ? (
                  <div className="order-actions">
                    <button
                      className="processed-btn"
                      onClick={() => handleProcessed(order._id)}
                      disabled={order.status === 'processed' || order.status === 'shipped'}
                    >
                      {order.status === 'processed' || order.status === 'shipped' ? 'Processed' : 'Mark as Processed'}
                    </button>

                    <button
                      className="shipped-btn"
                      onClick={() => handleShipped(order._id)}
                      disabled={order.status === 'shipped'}
                    >
                      {order.status === 'shipped' ? 'Shipped' : 'Mark as Shipped'}
                    </button>
                    <button
                        className="delete-btn"
                        onClick={() => handleDeleteOrder(order._id)}
                      >
                        🗑 Delete Order
                      </button>
                  </div>
                ) : (
                  <p style={{color:'blue', textAlign: 'center'}}>
                    <strong>
                      {order.viewerRelation === 'agent'
                        ? 'Manage this order'
                        : order.viewerRelation === 'buyer'
                        ? 'Use the tracker above to confirm receipt of your items'
                        : 'Submit the item/s'}
                    </strong>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersList;
